import React from 'react';
import {
    MdArticle,
    MdAudiotrack,
    MdClose,
    MdDelete,
    MdDescription,
    MdEdit,
    MdEditCalendar,
    MdEvent,
    MdPictureAsPdf,
    MdLanguage,
    MdMovie,
    MdSearch,
    MdTextSnippet,
    MdVisibility,
} from 'react-icons/md';
import { FaYoutube } from 'react-icons/fa';
import { LuFileText, LuGlobe as LuGlobe2 } from 'react-icons/lu';
import '../../Styles/AegisAi.css';

// Presentational building blocks shared by the Aegis AI pages (Knowledge, Assist).
// They hold no data logic — pages pass in already-computed values.

export const getInitials = (name) => {
    const parts = String(name || '').trim().split(/[\s@._-]+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const FILE_TYPES = {
    pdf: { color: '#d64545', Icon: MdPictureAsPdf },
    doc: { color: '#2b6cc4', Icon: MdArticle },
    docx: { color: '#2b6cc4', Icon: MdArticle },
    txt: { color: '#5b6f75', Icon: LuFileText },
    md: { color: '#3c9fa2', Icon: LuFileText },
    mp3: { color: '#8a4fd6', Icon: MdAudiotrack },
    wav: { color: '#8a4fd6', Icon: MdAudiotrack },
    m4a: { color: '#8a4fd6', Icon: MdAudiotrack },
    aac: { color: '#8a4fd6', Icon: MdAudiotrack },
    ogg: { color: '#8a4fd6', Icon: MdAudiotrack },
    flac: { color: '#8a4fd6', Icon: MdAudiotrack },
    mp4: { color: '#d9822b', Icon: MdMovie },
    mov: { color: '#d9822b', Icon: MdMovie },
    webm: { color: '#d9822b', Icon: MdMovie },
    mkv: { color: '#d9822b', Icon: MdMovie },
    avi: { color: '#d9822b', Icon: MdMovie },
};

// Must match MAX_SOURCE_BYTES in the API (helper/source_extraction_helper.py) — applies to every file source.
export const AI_SOURCE_MAX_BYTES = 200 * 1024 * 1024;

// Knowledge sources a client's RAG can ingest. File sources upload a file; link sources submit a URL.
export const AI_SOURCE_TYPES = [
    {
        key: 'document',
        label: 'Document',
        caption: 'PDF, DOCX',
        kind: 'file',
        Icon: MdDescription,
        color: '#2b6cc4',
        extensions: ['pdf', 'docx'],
    },
    {
        key: 'audio',
        label: 'Audio',
        caption: 'MP3, WAV, M4A, AAC, OGG, FLAC',
        kind: 'file',
        Icon: MdAudiotrack,
        color: '#8a4fd6',
        extensions: ['mp3', 'wav', 'm4a', 'aac', 'ogg', 'flac'],
    },
    {
        key: 'video',
        label: 'Video',
        caption: 'MP4, MOV, WEBM, MKV, AVI',
        kind: 'file',
        Icon: MdMovie,
        color: '#d9822b',
        extensions: ['mp4', 'mov', 'webm', 'mkv', 'avi'],
    },
    {
        key: 'youtube',
        label: 'YouTube',
        caption: 'Video link',
        kind: 'url',
        Icon: FaYoutube,
        color: '#e52d27',
        placeholder: 'https://www.youtube.com/watch?v=...',
        pattern: /^https?:\/\/(www\.|m\.|music\.)?(youtube\.com\/(watch\?.*v=|shorts\/|live\/|embed\/)|youtu\.be\/)[\w-]{6,}/i,
        invalidMessage: 'Enter a valid YouTube video URL',
    },
    {
        key: 'website',
        label: 'Website',
        caption: 'Web page link',
        kind: 'url',
        Icon: LuGlobe2,
        color: '#3c9fa2',
        placeholder: 'https://example.com/page',
        pattern: /^https?:\/\/[^\s/$.?#][^\s]*\.[^\s]{2,}$/i,
        invalidMessage: 'Enter a valid website URL starting with http:// or https://',
    },
    {
        key: 'custom_text',
        label: 'Custom Text',
        caption: 'Raw text, notes, FAQ',
        kind: 'text',
        Icon: LuFileText,
        color: '#0d9488',
        placeholder: 'Type or paste custom text or notes to feed into RAG...',
    },
];

export const getAiSourceType = (key) => AI_SOURCE_TYPES.find((type) => type.key === key) || AI_SOURCE_TYPES[0];

// Older records only carry a FileName, so infer the source from its extension when no type was stored.
export const inferAiSourceType = ({ sourceType, url, fileName } = {}) => {
    const stored = String(sourceType || '').toLowerCase();
    if (AI_SOURCE_TYPES.some((type) => type.key === stored)) return stored;
    if (stored === 'text' || stored === 'custom_text') return 'custom_text';
    if (fileName && (fileName.includes('custom-') || fileName.endsWith('-text.md') || fileName.startsWith('custom_'))) return 'custom_text';
    if (url) return getAiSourceType('youtube').pattern.test(url) ? 'youtube' : 'website';
    if (fileName && (fileName.includes('youtube') || fileName.includes('youtu.be'))) return 'youtube';
    if (fileName && (fileName.includes('-www-') || fileName.includes('http') || fileName.includes('shopify'))) return 'website';
    const ext = (String(fileName || '').match(/\.([a-z0-9]+)$/i)?.[1] || '').toLowerCase();
    const byExt = AI_SOURCE_TYPES.find((type) => type.extensions?.includes(ext));
    return byExt ? byExt.key : 'document';
};

export const formatFileSize = (bytes) => {
    const value = Number(bytes);
    if (!Number.isFinite(value) || value < 0) return null;
    if (value < 1024) return `${value} B`;
    const units = ['KB', 'MB', 'GB'];
    let size = value / 1024;
    let unit = 0;
    while (size >= 1024 && unit < units.length - 1) {
        size /= 1024;
        unit += 1;
    }
    return `${size.toFixed(size >= 100 ? 0 : 1)} ${units[unit]}`;
};

export const getFileMeta = (fileName) => {
    const match = String(fileName || '').match(/\.([a-z0-9]+)$/i);
    const ext = match ? match[1].toLowerCase() : '';
    const meta = FILE_TYPES[ext] || { color: '#3c9fa2', Icon: MdDescription };
    return { ...meta, ext: ext ? ext.toUpperCase() : '' };
};

export function AiSearchBox({ value, onChange, placeholder }) {
    return (
        <div className="hc-ai-search position-relative">
            <span className="hc-ai-search-icon position-absolute d-inline-flex">
                <MdSearch size={19} />
            </span>
            <input type="text" placeholder={placeholder} value={value} onChange={(event) => onChange(event.target.value)} aria-label={placeholder} />
            {value ? (
                <button type="button" aria-label="Clear search" className="hc-ai-search-clear position-absolute border-0 rounded-circle d-inline-flex align-items-center justify-content-center text-white" onClick={() => onChange('')}>
                    <MdClose size={15} />
                </button>
            ) : null}
        </div>
    );
}

export function AiStatusPill({ tone = 'inactive', label, large = false, icon: Icon }) {
    return (
        <span className={`hc-ai-status d-inline-flex align-items-center justify-content-center rounded-pill fw-bold text-nowrap hc-ai-status-${tone} ${large ? 'hc-ai-status-lg' : ''}`}>
            {Icon ? <Icon size={14} /> : null}
            {label}
        </span>
    );
}

export function AiRowActions({ onView, onEdit, onDelete }) {
    return (
        <div className="hc-ai-actions d-inline-flex align-items-center justify-content-center">
            {onView ? (
                <button type="button" title="View" aria-label="View" className="hc-ai-action border-0 d-inline-flex align-items-center justify-content-center hc-ai-action-view" onClick={onView}>
                    <MdVisibility size={16} />
                </button>
            ) : null}
            {onEdit ? (
                <button type="button" title="Edit" aria-label="Edit" className="hc-ai-action border-0 d-inline-flex align-items-center justify-content-center hc-ai-action-edit" onClick={onEdit}>
                    <MdEdit size={16} />
                </button>
            ) : null}
            {onDelete ? (
                <button type="button" title="Delete" aria-label="Delete" className="hc-ai-action border-0 d-inline-flex align-items-center justify-content-center hc-ai-action-delete" onClick={onDelete}>
                    <MdDelete size={16} />
                </button>
            ) : null}
        </div>
    );
}

export function AiCard({ icon: Icon, title, subtitle, action, children, className = '', hover = false }) {
    return (
        <div className={`hc-ai-card overflow-hidden ${hover ? 'hc-ai-card-hover' : ''} ${className}`}>
            <div className="hc-ai-card-head d-flex align-items-center">
                {Icon ? (
                    <span className="hc-ai-card-head-icon d-inline-flex align-items-center justify-content-center flex-shrink-0">
                        <Icon size={20} />
                    </span>
                ) : null}
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <h6 className="hc-ai-card-title m-0">{title}</h6>
                    {subtitle ? <p className="hc-ai-card-sub">{subtitle}</p> : null}
                </div>
                {action}
            </div>
            <div className="hc-ai-card-body">{children}</div>
        </div>
    );
}

export function AiFormSection({ icon: Icon, title, subtitle, action, children }) {
    return (
        <section className="hc-ai-form-section">
            <div className="hc-ai-form-section-head d-flex align-items-center mb-3">
                {Icon ? (
                    <span className="hc-ai-card-head-icon d-inline-flex align-items-center justify-content-center flex-shrink-0">
                        <Icon size={19} />
                    </span>
                ) : null}
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <h6 className="hc-ai-card-title m-0">{title}</h6>
                    {subtitle ? <p className="hc-ai-card-sub">{subtitle}</p> : null}
                </div>
                {action}
            </div>
            {children}
        </section>
    );
}

export function AiActivityTimeline({ createdOn, createdBy, modifiedOn, modifiedBy }) {
    const items = [
        { key: 'created', kicker: 'Created', icon: MdEvent, date: createdOn, by: createdBy, gold: false },
        { key: 'modified', kicker: 'Last modified', icon: MdEditCalendar, date: modifiedOn, by: modifiedBy, gold: true },
    ];
    return (
        <ul className="hc-ai-timeline m-0 p-0">
            {items.map(({ key, kicker, icon: Icon, date, by, gold }) => (
                <li key={key} className="hc-ai-tl-item position-relative d-flex">
                    <span className={`hc-ai-avatar rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 text-white hc-ai-avatar-lg ${gold ? 'hc-ai-avatar-gold' : ''}`} title={by || undefined}>
                        {by ? getInitials(by) : <Icon size={18} />}
                    </span>
                    <div className="hc-ai-tl-content">
                        <div className="hc-ai-tl-kicker text-uppercase">{kicker}</div>
                        <div className="hc-ai-tl-date d-flex align-items-center fw-bold">
                            <Icon size={15} className="flex-shrink-0" style={{ color: 'var(--ai-teal-600)' }} />
                            <span>{date || '-'}</span>
                        </div>
                        <div className="hc-ai-tl-by mt-1">
                            by <strong>{by || '-'}</strong>
                        </div>
                    </div>
                </li>
            ))}
        </ul>
    );
}

// Card-shaped placeholder: header (icon, title, subtitle) and body lines.
export function AiCardSkeleton({ bodyHeight = 160 }) {
    return (
        <div className="hc-ai-card overflow-hidden h-auto">
            <div className="hc-ai-card-head d-flex align-items-center">
                <div className="hc-ai-skel overflow-hidden flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 12 }} />
                <div className="flex-grow-1">
                    <div className="hc-ai-skel overflow-hidden mb-2" style={{ height: 16, width: '40%' }} />
                    <div className="hc-ai-skel overflow-hidden" style={{ height: 12, width: '65%' }} />
                </div>
            </div>
            <div className="hc-ai-card-body">
                <div className="hc-ai-skel overflow-hidden" style={{ height: bodyHeight, borderRadius: 14 }} />
            </div>
        </div>
    );
}

// children replaces the default placeholders below the hero.
export function AiViewSkeleton({ children }) {
    return (
        <div className="container-fluid py-4 hc-ai-page">
            <div className="d-flex justify-content-between mb-3">
                <div className="hc-ai-skel overflow-hidden" style={{ height: 42, width: 110, borderRadius: 999 }} />
                <div className="hc-ai-skel overflow-hidden" style={{ height: 42, width: 140 }} />
            </div>
            <div className="hc-ai-hero overflow-hidden text-white hc-ai-hero-lg mb-4">
                <div className="d-flex gap-3 align-items-center">
                    <div className="hc-ai-skel overflow-hidden flex-shrink-0" style={{ width: 64, height: 64, borderRadius: 18 }} />
                    <div className="flex-grow-1">
                        <div className="hc-ai-skel overflow-hidden mb-3" style={{ height: 14, width: 120, borderRadius: 999 }} />
                        <div className="hc-ai-skel overflow-hidden mb-2" style={{ height: 28, width: '55%' }} />
                        <div className="hc-ai-skel overflow-hidden" style={{ height: 16, width: '38%' }} />
                    </div>
                </div>
            </div>
            {children || (
                <div className="row g-4">
                    <div className="col-lg-7">
                        <div className="hc-ai-skel overflow-hidden" style={{ height: 280, borderRadius: 18 }} />
                    </div>
                    <div className="col-lg-5">
                        <div className="hc-ai-skel overflow-hidden" style={{ height: 280, borderRadius: 18 }} />
                    </div>
                </div>
            )}
        </div>
    );
}



export { LuFileText, LuGlobe2 };
